/**
 * ClassificationResultOverlay Component
 * Displays classification results as an overlay on the video stream
 * with a close button to dismiss it. Shows multiple items in a condensed format.
 *
 * Requirements: 3.3, 15.2
 */

import { useState } from 'react';
import { ClassificationResult, Subscription } from '../types/index';
import { ThriftService } from '../services/ThriftService';
import './ClassificationResultOverlay.css';

interface ClassificationResultOverlayProps {
  results: ClassificationResult[];
  onClose: () => void;
  subscription?: Subscription | null;
  totalItemsDetected?: number;
  canSeeMultipleItems?: boolean;
  className?: string;
  onSaveFeedback?: (frameId: string, feedback: string) => Promise<void>;
  onSaveCriterionVote?: (frameId: string, criterionName: string, vote: 'up' | 'down') => Promise<void>;
  isThriftLinked?: boolean;
  imageBlobs?: Blob[];
}

function ClassificationResultOverlay({
  results,
  onClose,
  subscription,
  totalItemsDetected = 1,
  canSeeMultipleItems = true,
  className,
  onSaveFeedback,
  onSaveCriterionVote,
  isThriftLinked = false,
  imageBlobs,
}: ClassificationResultOverlayProps) {
  // Feedback state keyed by frame_id
  const [feedbackOpenFor, setFeedbackOpenFor] = useState<string | null>(null);
  const [feedbackDrafts, setFeedbackDrafts] = useState<Record<string, string>>({});
  const [feedbackLoading, setFeedbackLoading] = useState<Record<string, boolean>>({});
  const [feedbackErrors, setFeedbackErrors] = useState<Record<string, string | null>>({});
  const [savedFeedback, setSavedFeedback] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    results.forEach((r) => {
      if (r.user_feedback) initial[r.frame_id] = r.user_feedback;
    });
    return initial;
  });

  // THRIFT push state keyed by frame_id (admin-only, per result)
  const [thriftService] = useState(() => new ThriftService());
  const [thriftStatus, setThriftStatus] = useState<Record<string, 'idle' | 'loading' | 'success' | 'error'>>({});
  const [thriftError, setThriftError] = useState<Record<string, string | null>>({});

  // Criterion vote state keyed by "${frame_id}:${criterion_name}"
  const [criterionVotes, setCriterionVotes] = useState<Record<string, 'up' | 'down'>>(() => {
    const initial: Record<string, 'up' | 'down'> = {};
    results.forEach((r) => {
      r.criteria_results.forEach((cr) => {
        if (cr.vote) initial[`${r.frame_id}:${cr.criterion_name}`] = cr.vote;
      });
    });
    return initial;
  });
  const [criterionVoteLoading, setCriterionVoteLoading] = useState<Record<string, boolean>>({});

  const handleFeedbackSubmit = async (frameId: string) => {
    if (!onSaveFeedback) return;
    const draft = (feedbackDrafts[frameId] || '').trim();
    if (!draft) return;
    setFeedbackLoading((prev) => ({ ...prev, [frameId]: true }));
    setFeedbackErrors((prev) => ({ ...prev, [frameId]: null }));
    try {
      await onSaveFeedback(frameId, draft);
      setSavedFeedback((prev) => ({ ...prev, [frameId]: draft }));
      setFeedbackOpenFor(null);
    } catch (err) {
      setFeedbackErrors((prev) => ({
        ...prev,
        [frameId]: err instanceof Error ? err.message : 'Failed to save feedback',
      }));
    } finally {
      setFeedbackLoading((prev) => ({ ...prev, [frameId]: false }));
    }
  };

  const handleThriftPush = async (result: ClassificationResult) => {
    if (!imageBlobs || imageBlobs.length === 0) return;
    const fid = result.frame_id;
    setThriftStatus((prev) => ({ ...prev, [fid]: 'loading' }));
    setThriftError((prev) => ({ ...prev, [fid]: null }));
    try {
      await thriftService.push([result], imageBlobs);
      setThriftStatus((prev) => ({ ...prev, [fid]: 'success' }));
    } catch (err) {
      setThriftError((prev) => ({
        ...prev,
        [fid]: err instanceof Error ? err.message : 'Failed to push to THRIFT',
      }));
      setThriftStatus((prev) => ({ ...prev, [fid]: 'error' }));
    }
  };

  const handleCriterionVote = async (
    frameId: string,
    criterionName: string,
    vote: 'up' | 'down'
  ) => {
    if (!onSaveCriterionVote) return;
    const voteKey = `${frameId}:${criterionName}`;
    // Clicking the active vote again switches to the other (no toggle-off, always voted)
    if (criterionVoteLoading[voteKey]) return;
    setCriterionVoteLoading((prev) => ({ ...prev, [voteKey]: true }));
    try {
      await onSaveCriterionVote(frameId, criterionName, vote);
      setCriterionVotes((prev) => ({ ...prev, [voteKey]: vote }));
    } catch (err) {
      console.error('Failed to save criterion vote:', err);
    } finally {
      setCriterionVoteLoading((prev) => ({ ...prev, [voteKey]: false }));
    }
  };

  // Defensive filter: hide "No items detected" placeholders when real results exist
  const filteredResults = (() => {
    const real = results.filter(r => r.criteria_results.length > 0);
    return real.length > 0 ? real : results;
  })();

  if (!filteredResults || filteredResults.length === 0) return null;

  const getConfidenceClass = (confidence: number | null): string => {
    if (confidence == null) return '';
    const percentage = confidence * 100;
    if (percentage <= 50) return 'confidence-low';
    if (percentage < 80) return 'confidence-medium';
    return 'confidence-high';
  };

  const showUpgradePrompt = !canSeeMultipleItems && totalItemsDetected > 1 && subscription;

  return (
    <div className={`classification-result-overlay ${className || ''}`} onClick={onClose}>
      <div className="result-overlay-content" onClick={(e) => e.stopPropagation()}>
        <button
          className="result-overlay-close"
          onClick={onClose}
          aria-label="Close result"
        >
          ×
        </button>

        {/* Main header */}
        <div className="result-overlay-header">
          <h3 className="result-overlay-title">
            {filteredResults.length === 1 ? 'Classification Result' : `${filteredResults.length} Items Detected`}
          </h3>

          {/* Tier limitation notice in overlay */}
          {showUpgradePrompt && (
            <div className="overlay-tier-notice">
              <div className="overlay-tier-content">
                <span className="overlay-tier-icon">🔒</span>
                <div className="overlay-tier-text">
                  <strong>{totalItemsDetected} items detected!</strong> Your {subscription!.subscription_type} plan shows item 1 only.
                </div>
                <button
                  className="overlay-upgrade-button"
                  onClick={() => window.location.href = '/account'}
                >
                  Upgrade
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Results list - each result shown vertically */}
        <div className="results-list-overlay">
          {filteredResults.map((result, resultIndex) => (
            <div key={result.frame_id || resultIndex} className="result-item-overlay">
               {/* Item header with description and price */}
               {filteredResults.length > 1 && (
                 <div className="result-item-header">
                   <h4 className="result-item-title">
                     Item {resultIndex + 1}: {result.description || 'Classification Result'}
                     {subscription?.show_price && result.estimated_price != null && (
                       <span className="result-item-price"> (€{result.estimated_price.toFixed(2)})</span>
                     )}
                   </h4>
                 </div>
               )}

               {/* Single item header (when only one result) */}
               {filteredResults.length === 1 && (result.description || (subscription?.show_price && result.estimated_price != null)) && (
                 <div className="result-item-header">
                   <h4 className="result-item-title">
                     {result.description || 'Classification Result'}
                     {subscription?.show_price && result.estimated_price != null && (
                       <span className="result-item-price"> (€{result.estimated_price.toFixed(2)})</span>
                     )}
                   </h4>
                 </div>
               )}

              {/* Condensed criteria grid */}
              <div className="result-grid-condensed">
                {result.criteria_results.map((criterionResult, index) => {
                  const voteKey = `${result.frame_id}:${criterionResult.criterion_name}`;
                  const currentVote = criterionVotes[voteKey];
                  const isVoting = !!criterionVoteLoading[voteKey];
                  return (
                    <div key={index} className="result-card-condensed">
                      <div className="result-card-main-condensed">
                        <div className="result-card-label-row">
                          <div className="result-label-condensed">
                            {criterionResult.criterion_name}
                          </div>
                          {onSaveCriterionVote && (
                            <div className="criterion-vote-inline">
                              <button
                                className={`criterion-vote-btn${currentVote === 'up' ? ' vote-active-up' : ''}`}
                                onClick={() => handleCriterionVote(result.frame_id, criterionResult.criterion_name, 'up')}
                                disabled={isVoting || currentVote === 'up'}
                                aria-label={`Good: ${criterionResult.criterion_name}`}
                              >
                                👍
                              </button>
                              <button
                                className={`criterion-vote-btn${currentVote === 'down' ? ' vote-active-down' : ''}`}
                                onClick={() => handleCriterionVote(result.frame_id, criterionResult.criterion_name, 'down')}
                                disabled={isVoting || currentVote === 'down'}
                                aria-label={`Bad: ${criterionResult.criterion_name}`}
                              >
                                👎
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="result-value-with-confidence">
                          <span className="result-value-condensed">
                            {criterionResult.status === 'classified'
                              ? criterionResult.selected_category
                              : '—'}
                          </span>
                          <span className={`result-confidence-condensed ${getConfidenceClass(criterionResult.confidence)}`}>
                            {criterionResult.confidence != null
                              ? `${(criterionResult.confidence * 100).toFixed(0)}%`
                              : '—'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Per-result actions: feedback + THRIFT push */}
              <div className="result-item-actions">
                {/* Feedback section */}
                {onSaveFeedback && (
                  <div className="result-feedback-section">
                    {feedbackOpenFor === result.frame_id ? (
                      <div className="result-feedback-form">
                        <textarea
                          className="result-feedback-textarea"
                          rows={3}
                          maxLength={1000}
                          value={feedbackDrafts[result.frame_id] || ''}
                          onChange={(e) =>
                            setFeedbackDrafts((prev) => ({ ...prev, [result.frame_id]: e.target.value }))
                          }
                          placeholder="Describe what was wrong with this classification..."
                        />
                        <div className="result-feedback-char-count">
                          {(feedbackDrafts[result.frame_id] || '').length}/1000
                        </div>
                        {feedbackErrors[result.frame_id] && (
                          <div className="result-feedback-error">{feedbackErrors[result.frame_id]}</div>
                        )}
                        <div className="result-feedback-actions">
                          <button
                            className="result-feedback-cancel"
                            onClick={() => {
                              setFeedbackOpenFor(null);
                              setFeedbackErrors((prev) => ({ ...prev, [result.frame_id]: null }));
                            }}
                            disabled={!!feedbackLoading[result.frame_id]}
                          >
                            Cancel
                          </button>
                          <button
                            className="result-feedback-submit"
                            onClick={() => handleFeedbackSubmit(result.frame_id)}
                            disabled={
                              !!feedbackLoading[result.frame_id] ||
                              !(feedbackDrafts[result.frame_id] || '').trim()
                            }
                          >
                            {feedbackLoading[result.frame_id] ? 'Saving...' : 'Submit'}
                          </button>
                        </div>
                      </div>
                    ) : savedFeedback[result.frame_id] || result.user_feedback ? (
                      <div className="result-feedback-saved">
                        <span className="result-feedback-saved-label">Your feedback:</span>
                        <span className="result-feedback-saved-text">
                          {savedFeedback[result.frame_id] || result.user_feedback}
                        </span>
                        <button
                          className="result-feedback-edit-btn"
                          onClick={() => {
                            setFeedbackDrafts((prev) => ({
                              ...prev,
                              [result.frame_id]: savedFeedback[result.frame_id] || result.user_feedback || '',
                            }));
                            setFeedbackOpenFor(result.frame_id);
                          }}
                        >
                          Edit
                        </button>
                      </div>
                    ) : (
                      <button
                        className="result-feedback-toggle"
                        onClick={() => {
                          setFeedbackDrafts((prev) => ({ ...prev, [result.frame_id]: '' }));
                          setFeedbackOpenFor(result.frame_id);
                        }}
                      >
                        Leave feedback
                      </button>
                    )}
                  </div>
                )}

                {/* THRIFT push — available to linked users */}
                {isThriftLinked && imageBlobs && imageBlobs.length > 0 && (
                  <div className="thrift-push-item-section">
                    {thriftStatus[result.frame_id] === 'success' ? (
                      <div className="thrift-push-success">Submitted to THRIFT</div>
                    ) : (
                      <>
                        <button
                          className="thrift-push-btn"
                          onClick={() => handleThriftPush(result)}
                          disabled={thriftStatus[result.frame_id] === 'loading'}
                        >
                          {thriftStatus[result.frame_id] === 'loading' ? 'Pushing...' : 'Push to THRIFT'}
                        </button>
                        {thriftStatus[result.frame_id] === 'error' && thriftError[result.frame_id] && (
                          <div className="thrift-push-error">{thriftError[result.frame_id]}</div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ClassificationResultOverlay;
